import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS } from "app/theme";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MilkQualityPerFarmerPeriodData } from "../hooks/useInsightsDashboardQueries";
import {
  getSccZone,
  getTbcZone,
  SCC_THRESHOLDS,
  TBC_THRESHOLDS,
  ZONE_COLORS,
} from "../milkQualityThresholds";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

type ZoneCounts = { period: string; green: number; amber: number; red: number };

function buildDistribution(
  data: MilkQualityPerFarmerPeriodData[],
  metric: "scc" | "tbc"
): ZoneCounts[] {
  const getZone = metric === "scc" ? getSccZone : getTbcZone;
  const key =
    metric === "scc"
      ? "MilkQuality.somaticCellCount"
      : "MilkQuality.totalBacterialCount";

  const byPeriod = new Map<
    string,
    { green: number; amber: number; red: number }
  >();
  const farmerSeen = new Map<string, Set<string>>();

  for (const r of data) {
    const period = String(r["MilkQuality.periodStart"]).split("T")[0];
    const uid = r["MilkQuality.userGuid"];

    if (!farmerSeen.has(period)) farmerSeen.set(period, new Set());
    const seenSet = farmerSeen.get(period);
    if (seenSet?.has(uid)) continue;
    seenSet?.add(uid);

    if (!byPeriod.has(period))
      byPeriod.set(period, { green: 0, amber: 0, red: 0 });
    const counts = byPeriod.get(period);
    if (!counts) continue;
    const val = Number(r[key]);
    if (isNaN(val)) continue;
    const zone = getZone(val);
    counts[zone]++;
  }

  return Array.from(byPeriod.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, counts]) => ({ period, ...counts }));
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

interface Props {
  title: string;
  data: MilkQualityPerFarmerPeriodData[] | undefined;
  metric: "scc" | "tbc";
  isLoading: boolean;
}

export default function MilkQualityDistributionChart({
  title,
  data,
  metric,
  isLoading,
}: Props) {
  const thresholds = metric === "scc" ? SCC_THRESHOLDS : TBC_THRESHOLDS;
  const unit = metric === "scc" ? "k cells/ml" : "k cfu/ml";

  const chartData = useMemo(
    () => (data ? buildDistribution(data, metric) : []),
    [data, metric]
  );

  if (isLoading) {
    return (
      <Card sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", mb: 1.5, display: "block" }}
      >
        Number of farmers per threshold zone (green &lt;{thresholds.good}
        {unit}, amber {thresholds.good}–{thresholds.warning}
        {unit}, red &gt;{thresholds.warning}
        {unit})
      </Typography>
      <Box sx={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="period"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Farmers",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: "#71717a" },
              }}
            />
            <Tooltip
              labelFormatter={(label) => {
                const d = new Date(String(label));
                return d.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
              }}
              formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  green: `Good (<${thresholds.good})`,
                  amber: `Warning (${thresholds.good}–${thresholds.warning})`,
                  red: `Critical (>${thresholds.warning})`,
                };
                return [value, labels[name] ?? name];
              }}
            />
            <Area
              type="monotone"
              dataKey="green"
              stackId="zone"
              fill={ZONE_COLORS.green}
              fillOpacity={0.6}
              stroke={ZONE_COLORS.green}
              strokeWidth={0}
            />
            <Area
              type="monotone"
              dataKey="amber"
              stackId="zone"
              fill={ZONE_COLORS.amber}
              fillOpacity={0.6}
              stroke={ZONE_COLORS.amber}
              strokeWidth={0}
            />
            <Area
              type="monotone"
              dataKey="red"
              stackId="zone"
              fill={ZONE_COLORS.red}
              fillOpacity={0.6}
              stroke={ZONE_COLORS.red}
              strokeWidth={0}
            />
            <ReferenceLine y={0} stroke="#e5e5e5" />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}
